import {
  type PlanData,
  type PlanPeriodValue,
  type QuarterlyPeriodValue,
  type WeeklyPeriodValue,
  isQuarterlyPlan,
  isWeeklyPlan,
} from '@terros/common'
import { DateTime, Interval } from 'luxon'

const WEEK_PLAN_PERIOD_FORMAT = "'W'kkkk'|'WW"
const QUARTER_PLAN_PERIOD_FORMAT = "'Q'yyyy'|'q"

export const periodToDate = (period: PlanPeriodValue): DateTime => {
  if (isQuarterlyPlan(period)) {
    return DateTime.fromFormat(period, QUARTER_PLAN_PERIOD_FORMAT)
  }
  return DateTime.fromFormat(period, WEEK_PLAN_PERIOD_FORMAT)
}

export const getPlanDateRange = (period: PlanPeriodValue): Interval<true> => {
  const begin = periodToDate(period)
  if (isQuarterlyPlan(period)) {
    const interval = Interval.fromDateTimes(begin, begin.endOf('quarter'))
    if (!interval.isValid) throw new Error('Invalid date range')
    return interval
  }
  const interval = Interval.fromDateTimes(begin, begin.endOf('week'))
  if (!interval.isValid) throw new Error('Invalid date range')
  return interval
}

export const periodToString = (period: PlanPeriodValue): string => {
  if (isQuarterlyPlan(period)) {
    const currentQuarterDateTime = DateTime.fromFormat(period, QUARTER_PLAN_PERIOD_FORMAT)
    return `Q${currentQuarterDateTime.quarter} ${currentQuarterDateTime.year}`
  }
  const currentWeekDateTime = DateTime.fromFormat(period, WEEK_PLAN_PERIOD_FORMAT)
  return `Week of ${currentWeekDateTime.toLocaleString(DateTime.DATE_MED)}`
}

export const sortPlansByPeriod = (a: PlanData, b: PlanData): number => {
  const aDate = adjustedPeriodToDate(a.period)
  const bDate = adjustedPeriodToDate(b.period)
  return aDate.diff(bDate).milliseconds
}

export const getCurrentQuarterlyPeriod = (): QuarterlyPeriodValue => {
  return DateTime.now().toFormat('Qyyyy|q') as QuarterlyPeriodValue
}

export const getRelevantQuarterlyPeriod = (): QuarterlyPeriodValue => {
  const now = DateTime.now()
  const nextQuarterStart = now.plus({ quarters: 1 }).startOf('quarter')
  const monthsUntilNextQuarter = nextQuarterStart.diff(now, 'months').months
  if (monthsUntilNextQuarter <= 1) {
    return nextQuarterStart.toFormat('Qyyyy|q') as QuarterlyPeriodValue
  }
  return now.toFormat('Qyyyy|q') as QuarterlyPeriodValue
}

export const getCurrentWeeklyPeriod = (): WeeklyPeriodValue => {
  return DateTime.now().toFormat("'W'kkkk|WW") as WeeklyPeriodValue
}

/**
 * Ensures that weekly plans for the first week of a quarter appear after the quarterly plan.
 */
const adjustedPeriodToDate = (period: PlanPeriodValue): DateTime => {
  const date = periodToDate(period)
  if (isWeeklyPlan(period)) return date.plus({ millisecond: 1 })
  return date
}
